import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPills, FaTint, FaFlask, FaBandAid, FaStethoscope, FaMicroscope, FaSearch, FaTimes, FaWhatsapp, FaPhone, FaEnvelope } from 'react-icons/fa';
import { getCategories, getProducts } from '../services/api';
import './Products.css';

function Products() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const categoryIcons = {
    1: <FaPills />,
    2: <FaTint />,
    3: <FaFlask />,
    4: <FaBandAid />,
    5: <FaStethoscope />,
    6: <FaMicroscope />
  };

  const categoryRoutes = {
    1: '/products/prescription-drugs',
    2: '/products/blood-glucose',
    3: '/products/rapid-diagnostics',
    4: '/products/surgical',
    5: '/products/medical-instruments',
    6: '/products/laboratory'
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching data from API...');
        const [categoriesData, productsData] = await Promise.all([
          getCategories(),
          getProducts()
        ]);
        console.log('Categories data:', categoriesData);
        console.log('Products data:', productsData);
        setCategories(categoriesData);
        setAllProducts(productsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setCategories([]);
        setAllProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle search
  // Handle search
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      setIsSearching(false);
    } else {
      setIsSearching(true);
      const filtered = allProducts.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSearchResults(filtered);
    }
  }, [searchTerm, allProducts]);

  const clearSearch = () => {
    setSearchTerm('');
    setIsSearching(false);
    setSearchResults([]);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  return (
    <div className="products-page">
      <div className="products-container">
        <div className="products-header">
          <h1>Our Products</h1>
          <p className="products-subtitle">
            Quality pharmaceutical products and medical supplies for your healthcare needs
          </p>
          
          {/* Search Bar */}
          <div className="products-search-container">
            <div className="products-search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                className="products-search-input"
                placeholder="Search for products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="search-clear-btn" onClick={clearSearch}>
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Search Results */}
        {isSearching && (
          <div className="search-results-section">
            <h2>Search Results for "{searchTerm}"</h2>
            {searchResults.length > 0 ? (
              <div className="search-results-grid">
                {searchResults.map((product) => (
                  <div 
                    key={product.id} 
                    className="search-result-card"
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="search-result-image">
                      <img 
                        src={product.image_url} 
                        alt={product.name} 
                        loading="lazy"
                        decoding="async"
                        onError={(e) => e.target.src = '/pharmacare-logo.png'} 
                        style={{ maxWidth: '100%', height: 'auto' }}
                      />
                    </div>
                    <div className="search-result-info">
                      <h3>{product.name}</h3>
                      <p className="search-result-category">{getCategoryName(product.category_id)}</p>
                      <span className="click-to-view">Click to view details</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-results-section">
                <div className="no-results-icon">
                  <FaSearch />
                </div>
                <h3>No Products Found</h3>
                <p>We couldn't find any products matching "{searchTerm}"</p>
                <div className="contact-info-box">
                  <h4>📋 Note: Product Not Available?</h4>
                  <p>Can't find what you're looking for? Our team can help!</p>
                  <div className="contact-options">
                    <a href="https://wa.me/265" className="contact-btn whatsapp">
                      <FaWhatsapp /> WhatsApp Us
                    </a>
                    <a href="tel:+265" className="contact-btn phone">
                      <FaPhone /> Call Us
                    </a>
                    <a href="mailto:info@pharmacare.com" className="contact-btn email">
                      <FaEnvelope /> Email Us
                    </a>
                  </div>
                  <p className="contact-note">
                    💡 We may have the product in stock or can order it for you!
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Product Modal */}
        {selectedProduct && (
          <div className="product-modal-overlay" onClick={() => setSelectedProduct(null)}>
            <div className="product-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close-btn" onClick={() => setSelectedProduct(null)}>
                <FaTimes />
              </button>
              <div className="modal-content">
                <div className="modal-image">
                  <img src={selectedProduct.image_url} alt={selectedProduct.name} onError={(e) => e.target.src = '/pharmacare-logo.png'} />
                </div>
                <div className="modal-info">
                  <h2>{selectedProduct.name}</h2>
                  <p className="modal-category">
                    Category: <span>{getCategoryName(selectedProduct.category_id)}</span>
                  </p>
                  <div className="modal-actions">
                    <a 
                      href={`https://wa.me/265?text=Hi, I'm interested in ${selectedProduct.name}`}
                      className="modal-btn whatsapp"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaWhatsapp /> Inquire on WhatsApp
                    </a>
                    <button 
                      className="modal-btn secondary"
                      onClick={() => {
                        setSelectedProduct(null);
                        navigate(categoryRoutes[selectedProduct.category_id]);
                      }}
                    >
                      View Category
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Categories Grid */}
        {!isSearching && (
          <div className="products-grid">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading categories...</p>
            </div>
          ) : categories.length > 0 ? (
            categories.map((category) => (
              <div key={category.id} className="product-category-card">
                <div className="category-icon">{categoryIcons[category.id]}</div>
                <h2>{category.name}</h2>
                <p className="category-description">{category.description}</p>
                <div className="category-divider"></div>
                <ul className="products-list">
                  <li>View all products in this category</li>
                </ul>
                <button 
                  className="see-more-btn"
                  onClick={() => navigate(categoryRoutes[category.id])}
                >
                  See More
                </button>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>No categories found.</p>
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Products;
